import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { findPackOpportunity, priceCart, type PricePack } from '@greengrocer/shared';
import { apiFetch } from './api';

export interface AppliedPack {
  packUnits: number;
  packPriceCents: number;
  count: number;
}

export interface CartLine {
  storeItemId: string;
  name: string;
  emoji: string;
  quantityUnits: number;
  availableUnits: number;
  unitPriceCents: number;
  customerPriced: boolean;
  minPriceCents: number | null;
  maxPriceCents: number | null;
  packsApplied: AppliedPack[];
  looseUnits: number;
  lineTotalCents: number;
  lineDiscountCents: number;
  opportunity: { extraUnits: number; targetUnits: number; savingCents: number } | null;
  exceedsStock: boolean;
}

export interface Cart {
  lines: CartLine[];
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
}

/** The shelf data the browser needs to price a line without asking the server. */
export interface ShelfItem {
  id: string;
  name: string;
  emoji: string;
  /** On a customer-priced item this is the SUGGESTED price, not the charge. */
  unitPriceCents: number;
  availableUnits: number;
  packs: PricePack[];
  customerPriced: boolean;
  minPriceCents: number | null;
  maxPriceCents: number | null;
}

/** One line's quantity and, where the buyer set it, their chosen unit price. */
interface Entry {
  storeItemId: string;
  quantityUnits: number;
  unitPriceCents?: number;
}

const EMPTY: Cart = { lines: [], subtotalCents: 0, discountCents: 0, totalCents: 0 };

/**
 * Rebuilds a whole priced cart from nothing but quantities.
 *
 * This is the same `priceCart` the server runs at the till, imported from the
 * shared package — which is what makes an optimistic update honest rather than a
 * guess. The line total, the pack breakdown and the basket total are all real
 * numbers the moment the button is pressed; the server's reply confirms them
 * rather than revealing them. A cart that showed a spinner where the price goes
 * would be no better than waiting for the round trip.
 */
function repriceCart(
  quantities: readonly Entry[],
  shelf: ReadonlyMap<string, ShelfItem>,
): Cart {
  const rows = quantities.flatMap((entry) => {
    const item = shelf.get(entry.storeItemId);
    if (!item || entry.quantityUnits <= 0) return [];
    return [{ entry, item }];
  });

  const inputs = rows.map(({ entry, item }) => ({
    quantityUnits: entry.quantityUnits,
    // On a customer-priced line the buyer's figure IS the unit price; the
    // shop's is only the suggestion the input box opened on.
    unitPriceCents: item.customerPriced
      ? (entry.unitPriceCents ?? item.unitPriceCents)
      : item.unitPriceCents,
    packs: item.customerPriced ? [] : item.packs,
  }));

  const priced = priceCart(inputs);

  return {
    lines: rows.map(({ entry, item }, index) => {
      const line = priced.lines[index];
      const input = inputs[index];
      return {
        storeItemId: entry.storeItemId,
        name: item.name,
        emoji: item.emoji,
        quantityUnits: entry.quantityUnits,
        availableUnits: item.availableUnits,
        unitPriceCents: input?.unitPriceCents ?? item.unitPriceCents,
        customerPriced: item.customerPriced,
        minPriceCents: item.minPriceCents,
        maxPriceCents: item.maxPriceCents,
        packsApplied: line ? [...line.packsApplied] : [],
        looseUnits: line?.looseUnits ?? entry.quantityUnits,
        lineTotalCents: line?.lineTotalCents ?? 0,
        lineDiscountCents: line?.lineDiscountCents ?? 0,
        opportunity: input ? findPackOpportunity(input) : null,
        exceedsStock: entry.quantityUnits > item.availableUnits,
      };
    }),
    subtotalCents: priced.subtotalCents,
    discountCents: priced.discountCents,
    totalCents: priced.totalCents,
  };
}

/** Applies a delta to one line and reprices, clamped to what the shelf holds. */
function withDelta(
  current: Cart | undefined,
  shelf: ReadonlyMap<string, ShelfItem>,
  storeItemId: string,
  deltaUnits: number,
  chosenPriceCents?: number,
): Cart {
  const lines = current?.lines ?? [];
  const existing = lines.find((line) => line.storeItemId === storeItemId);
  const available = shelf.get(storeItemId)?.availableUnits ?? 0;
  // Falls back to what is already on the line, so the basket steppers change
  // the quantity without restating the price the buyer chose.
  const price = chosenPriceCents ?? existing?.unitPriceCents;
  // Clamped here so the number never shows a quantity the shop cannot fill and
  // then snaps back when the server refuses it. The server re-checks against a
  // locked row regardless; this is only about not flashing a lie.
  const next = Math.min((existing?.quantityUnits ?? 0) + deltaUnits, available);

  const quantities: Entry[] = lines.map((line) =>
    line.storeItemId === storeItemId
      ? { storeItemId, quantityUnits: next, ...(price === undefined ? {} : { unitPriceCents: price }) }
      : {
          storeItemId: line.storeItemId,
          quantityUnits: line.quantityUnits,
          unitPriceCents: line.unitPriceCents,
        },
  );
  if (!existing) {
    quantities.push({
      storeItemId,
      quantityUnits: next,
      ...(price === undefined ? {} : { unitPriceCents: price }),
    });
  }

  return repriceCart(quantities, shelf);
}

function withoutLine(
  current: Cart | undefined,
  shelf: ReadonlyMap<string, ShelfItem>,
  storeItemId: string,
): Cart {
  const remaining: Entry[] = (current?.lines ?? [])
    .filter((line) => line.storeItemId !== storeItemId)
    .map((line) => ({
      storeItemId: line.storeItemId,
      quantityUnits: line.quantityUnits,
      unitPriceCents: line.unitPriceCents,
    }));
  return repriceCart(remaining, shelf);
}

/**
 * The basket, its two write actions, and the optimism that makes them feel free.
 *
 * Writes are DELTAS, never absolute quantities, because taps race: five "+1"
 * requests in flight must add up to five, where five "set it to 2" requests
 * would all land on two. That the operation commutes is exactly what makes it
 * safe to apply locally before the server has agreed.
 *
 * A refetch is issued only when the LAST in-flight write settles. Reconciling
 * after every one of them would let an early reply, computed before the later
 * taps committed, overwrite the newer state and make the count jump backwards.
 */
export function useCart(storeId: string, items: readonly ShelfItem[] | undefined) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const cartKey = useMemo(() => ['cart', storeId], [storeId]);
  const writeKey = useMemo(() => ['cart-write', storeId], [storeId]);

  const shelf = useMemo(
    () => new Map((items ?? []).map((item) => [item.id, item])),
    [items],
  );

  const query = useQuery({
    queryKey: cartKey,
    queryFn: () => apiFetch<Cart>(`/api/stores/${storeId}/cart`),
  });

  /** Resyncs once the burst is over. `=== 1` counts the mutation now settling. */
  const settle = async () => {
    if (queryClient.isMutating({ mutationKey: writeKey }) === 1) {
      await queryClient.invalidateQueries({ queryKey: cartKey });
    }
  };

  const adjust = useMutation({
    mutationKey: writeKey,
    mutationFn: (input: { storeItemId: string; deltaUnits: number; unitPriceCents?: number }) =>
      apiFetch<Cart>(`/api/stores/${storeId}/cart/items`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onMutate: async (input) => {
      setProblem(null);
      // Stops an in-flight GET from landing after this and undoing it.
      await queryClient.cancelQueries({ queryKey: cartKey });
      queryClient.setQueryData<Cart>(cartKey, (current) =>
        withDelta(current, shelf, input.storeItemId, input.deltaUnits, input.unitPriceCents),
      );
    },
    onError: (error: Error) => setProblem(error.message),
    onSettled: settle,
  });

  const removeAll = useMutation({
    mutationKey: writeKey,
    mutationFn: (storeItemId: string) =>
      apiFetch<Cart>(`/api/stores/${storeId}/cart/items/${storeItemId}`, { method: 'DELETE' }),
    onMutate: async (storeItemId) => {
      setProblem(null);
      await queryClient.cancelQueries({ queryKey: cartKey });
      queryClient.setQueryData<Cart>(cartKey, (current) =>
        withoutLine(current, shelf, storeItemId),
      );
    },
    onError: (error: Error) => setProblem(error.message),
    onSettled: settle,
  });

  const buy = useMutation({
    mutationFn: () =>
      apiFetch<{ reference: string; totalCents: number }>(`/api/stores/${storeId}/purchase`, {
        method: 'POST',
        body: '{}',
      }),
    onError: (error: Error) => setProblem(error.message),
    onSuccess: async () => {
      setProblem(null);
      // Stock moved, so the shelf is stale as well as the basket.
      queryClient.setQueryData<Cart>(cartKey, EMPTY);
      await queryClient.invalidateQueries({ queryKey: ['items', storeId] });
      await queryClient.invalidateQueries({ queryKey: cartKey });
    },
  });

  return {
    cart: query.data ?? EMPTY,
    isLoading: query.isPending,
    problem,
    clearProblem: () => setProblem(null),
    adjust: (storeItemId: string, deltaUnits: number, unitPriceCents?: number) =>
      adjust.mutate({
        storeItemId,
        deltaUnits,
        ...(unitPriceCents === undefined ? {} : { unitPriceCents }),
      }),
    removeAll: (storeItemId: string) => removeAll.mutate(storeItemId),
    buy,
  };
}
